import {
  useQuery,
  type UseQueryOptions,
  useMutation,
  type UseMutationOptions,
} from "@tanstack/react-query";

import {
  rootAccountsEndpoint,
  rootCallEvaluationEndpoint,
  rootEmployeeManagementEndpoint,
} from "../../core/apiurl";
import { buildQueryParams } from "../../utils/commonUtils";
import type { TApiServiceError } from "../apiService";

import type { TUser } from "../user";
import { commonService } from "./common.service";
import type {
  TCampaignsResponse,
  TEntityFeatureShoHideFlagResponse,
  TServicesResponse,
  TExcelExportResponse,
  TUserDeactivationResponse,
  TUploadResponse,
  TLanguageListResponse,
  TEmployeeAgentListRes,
  TCountryResponse,
  TDateTimeSettingsData,
  TDateLocationListResponse,
  TLocationListResponse,
  TEntityResponse,
  TUpdatePrimaryRoleRequestPayload,
  TUpdatePrimaryRoleResponsePayload,
  TFailedCategoryCountsResponseNew,
  TEmployeeSupervisorListResponseSchema,
  TTemplateLookupResponse,
  TEmployeeListBasedRankResponseSchema,
} from "./common.types";
import type { TQASupervisorListResponse } from "../manage-users/manage-users.types";

// ✅ Constants
export const GET_POSTS_CALLEVALUATION_QUERY_KEY = rootCallEvaluationEndpoint;
export const GET_POSTS_ACCOUNTS_QUERY_KEY = rootAccountsEndpoint;

// ✅ Common helper to avoid "no-return-await"
export const safeAsync = async <T>(fn: () => Promise<T>): Promise<T> => fn();

/* ---------------------- QUERY HOOKS ---------------------- */

export const useGetCampaignDataQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TCampaignsResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_CALLEVALUATION_QUERY_KEY, "campaigns-list", params],
    queryFn: () => {
      const query = buildQueryParams(params);
      return safeAsync(() => commonService.getCampaigns(query));
    },
    ...options,
  });

export const useGetCountryList = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TCountryResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_ACCOUNTS_QUERY_KEY, "country-list", params],
    queryFn: () => {
      const query = buildQueryParams(params);
      return safeAsync(() => commonService.getCountryList(query));
    },
    ...options,
  });

export const useGetStateList = (
  params?: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TCountryResponse | null, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_ACCOUNTS_QUERY_KEY, "state-list", params?.country_id],
    queryFn: () => {
      if (!params?.country_id) return Promise.resolve(null);
      return safeAsync(() =>
        commonService.getStateList(params.country_id as string),
      );
    },
    enabled: Boolean(params?.country_id),
    ...options,
  });

export const useGetServicesDataQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TServicesResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_CALLEVALUATION_QUERY_KEY, "services-list", params],
    queryFn: () => {
      const query = buildQueryParams(params);
      return safeAsync(() => commonService.getServices(query));
    },
    ...options,
  });

export const useGetEntityDataQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TEntityResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_ACCOUNTS_QUERY_KEY, "entity-list", params],
    queryFn: () => {
      const query = buildQueryParams(params);
      const hasParams = Object.keys(params).length > 0;
      return safeAsync(() =>
        commonService.getEntityList({
          searchParams: hasParams ? query : undefined,
        }),
      );
    },
    ...options,
  });

export const useGetEntityFeatureShowHideFlagDataQuery = (
  params: string | Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TEntityFeatureShoHideFlagResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [
      GET_POSTS_ACCOUNTS_QUERY_KEY,
      "entityFeatureShowHideFlag",
      params,
    ],
    queryFn: () => {
      const query =
        typeof params === "string" ? params : buildQueryParams(params);
      return safeAsync(() => commonService.getEntityFeatureShowHideFlag(query));
    },
    ...options,
  });

export const useGetUserDetailsQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TUser, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) => {
  const config = {
    keepPreviousData: false,
    staleTime: 0,
    ...options,
  };
  return useQuery({
    queryKey: [GET_POSTS_CALLEVALUATION_QUERY_KEY, "user-details", params],
    queryFn: () => {
      const query = buildQueryParams(params);
      return safeAsync(() => commonService.getUserDetails(query));
    },
    ...config,
  });
};

/**
 * Custom hook to fetch location list using React Query.
 * @param params - A record of key-value pairs representing query parameters.
 * @param options - Optional configuration for the React Query `useQuery` hook, excluding `queryKey` and `queryFn`.
 * @returns - The result of the `useQuery` hook for fetching location list.
 */
export const useGetLocationListQuery = (
  params: Record<string, any>,
  options?: Omit<
    UseQueryOptions<TLocationListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery({
    queryKey: [GET_POSTS_CALLEVALUATION_QUERY_KEY, "location", params],
    queryFn: async () => {
      const query = buildQueryParams(params);
      return await commonService.getLocationList(query);
    },
    ...options,
  });
};
/* ---------------------- MUTATION HOOKS ---------------------- */

export const useUpdateLocation = (
  options?: Omit<
    UseMutationOptions<
      TDateTimeSettingsData,
      TApiServiceError,
      TDateTimeSettingsData
    >,
    "mutationFn"
  >,
) =>
  useMutation({
    mutationFn: (payload) => commonService.updateLocation(payload),
    ...options,
  });

export const useDeleteLocation = (
  options?: Omit<
    UseMutationOptions<TLocationListResponse, TApiServiceError, string>,
    "mutationFn"
  >,
) =>
  useMutation({
    mutationFn: async (deleteId) =>
      safeAsync(() => commonService.deleteLocation(deleteId)),
    ...options,
  });

export const useUserDeactivationMutation = (
  options?: Omit<
    UseMutationOptions<TUserDeactivationResponse, TApiServiceError, string>,
    "mutationFn"
  >,
) =>
  useMutation({
    mutationFn: async (query) =>
      safeAsync(() => commonService.getUserDeactivation(query)),
    ...options,
  });

export const useFileUploadMutation = (
  options?: Omit<
    UseMutationOptions<TUploadResponse, TApiServiceError, File>,
    "mutationFn"
  >,
) =>
  useMutation({
    mutationFn: async (file) => safeAsync(() => commonService.uploadFile(file)),
    ...options,
  });

/* ---------------------- OTHER QUERIES ---------------------- */

export const useGetDateTimeLocationListQuery = (
  options?: Omit<
    UseQueryOptions<TDateLocationListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_ACCOUNTS_QUERY_KEY, "date-time-location-list"],
    queryFn: () => safeAsync(() => commonService.getLocationsList()),
    ...options,
  });

export const useGetExcelExportQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TExcelExportResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_CALLEVALUATION_QUERY_KEY, "excel-export", params],
    queryFn: () => {
      const query = buildQueryParams(params);
      return safeAsync(() => commonService.getExcelExport(query));
    },
    ...options,
  });

export const useGetAgentListQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TQASupervisorListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_ACCOUNTS_QUERY_KEY, "agent-list", params],
    queryFn: () => {
      const query = params ? buildQueryParams(params) : "";
      return safeAsync(() => commonService.getAgentList(query));
    },
    ...options,
  });

export const useGetEmployeeAgentListQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TEmployeeAgentListRes, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_ACCOUNTS_QUERY_KEY, "employee-agent-list", params],
    queryFn: () => {
      const query = params ? buildQueryParams(params) : "";
      return safeAsync(() => commonService.getEmployeeAgentList(query));
    },
    ...options,
  });

/**
 * Custom hook to fetch QA supervisor list using React Query for filter popover.
 * Uses the qa_supervisor API endpoint.
 *
 * @param params - A record of key-value pairs representing query parameters.
 * @param options - Optional configuration for the React Query `useQuery` hook, excluding `queryKey` and `queryFn`.
 * @returns The result of the `useQuery` hook for fetching QA supervisor list.
 */
export const useGetQASupervisorListQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TQASupervisorListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_ACCOUNTS_QUERY_KEY, "qa-supervisor-list", params],
    queryFn: () => {
      const query = params ? buildQueryParams(params) : "";
      return safeAsync(() => commonService.getQASupervisorList(query));
    },
    ...options,
  });

export const useGetLanguageListQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TLanguageListResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_CALLEVALUATION_QUERY_KEY, "language-list", params],
    queryFn: () => {
      const query = buildQueryParams(params);
      return safeAsync(() => commonService.getLanguageList(query));
    },
    ...options,
  });

export const useUpdatePrimaryRoleMutation = (
  options?: Omit<
    UseMutationOptions<
      TUpdatePrimaryRoleResponsePayload,
      TApiServiceError,
      TUpdatePrimaryRoleRequestPayload
    >,
    "mutationFn"
  >,
) =>
  useMutation({
    mutationFn: (payload) => commonService.updatePrimaryRole(payload),
    ...options,
  });

// ---------------  Failed Category Counts  ---------------------
export const useGetFailedCategoryCountsDataQuery = (
  params: Record<string, any>,
  options?: Omit<
    UseQueryOptions<TFailedCategoryCountsResponseNew, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) => {
  return useQuery({
    queryKey: ["failedCategoryCountsData", params], // ensures unique cache per filter
    queryFn: () => {
      const query = buildQueryParams(params);
      return safeAsync(() => commonService.failedCategoryCounts(query)); // Pass to service
    },
    ...options,
  });
};

// ---------------------------Employee Supervisor list------------
export const useGetEmployeeSupervisorListQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TEmployeeSupervisorListResponseSchema, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [
      GET_POSTS_ACCOUNTS_QUERY_KEY,
      "employee-supervisor-list",
      params,
    ],
    queryFn: () => {
      const query = params ? buildQueryParams(params) : "";
      return safeAsync(() => commonService.getEmployeeSupervisorList(query));
    },
    ...options,
  });

export const useGetTemplateLookupQuery = (
  options?: Omit<
    UseQueryOptions<TTemplateLookupResponse, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [GET_POSTS_CALLEVALUATION_QUERY_KEY, "template-lookup"],
    queryFn: () => safeAsync(() => commonService.getTemplateLookup()),
    ...options,
  });

export const useGetEmployeeListBasedRankQuery = (
  params: Record<string, unknown>,
  options?: Omit<
    UseQueryOptions<TEmployeeListBasedRankResponseSchema, TApiServiceError>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    queryKey: [rootEmployeeManagementEndpoint, "employee-list-rank", params],
    queryFn: async () => {
      const query = params ? buildQueryParams(params) : "";
      return await commonService.getEmployeeListRank(query);
    },
    ...options,
  });
